// Buildings tab (spec §3.3): the build queue and the slice facilities in groups.
import type {
  BuildingQueueItem,
  FacilityType,
  FullSystem,
  Resources,
  UpgradeBlocker,
  UpgradeOption,
} from '@stars-and-ships/api-types';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { FACILITY_GROUPS, facilityEffect } from '../../facilities';
import { FACILITY_LABELS, formatDuration, LATER_FACILITIES } from '../../labels';
import { affordable } from '../../state/live';
import { isPendingId } from '../../state/systemCache';
import { BuildingInfo } from '../codex/BuildingInfo';
import { Button, CostList, IconButton, Panel } from '../ui';
import { FinishNowButton } from './FinishNow';
import { PlanetsLine } from './shared';
import type { SystemActions } from './types';

export function blockerText(blocker: UpgradeBlocker, facility: FacilityType): string {
  switch (blocker.code) {
    case 'QUEUE_SLOTS_FULL':
      return 'Queue full';
    case 'LEVEL_CAP_REACHED':
      return 'Max level';
    case 'REQUIREMENTS_NOT_MET':
      if (blocker.requirement === 'command_center') {
        return `Needs Command Center Lv ${blocker.requiredLevel}`;
      }
      return LATER_FACILITIES.has(facility) ? 'Later build' : 'Needs Artifacts (later build)';
  }
}

/** The one build queue (G-57): the first upgrade builds, the second waits and starts after it. */
export function QueuePanel({
  queue,
  now,
  actions,
}: {
  queue: BuildingQueueItem[];
  now: number;
  actions: SystemActions | undefined;
}) {
  const ordered = [...queue].sort((a, b) => a.slot - b.slot);
  return (
    <Panel title="Build queue">
      <ul className="space-y-2">
        {ordered.map((item, index) => {
          const start = Date.parse(item.startedAt);
          const end = Date.parse(item.completesAt);
          // Only an entry behind another one waits; `now` ticks once a second, so a fresh first
          // entry would otherwise look "waiting" for a moment.
          const waiting = index > 0 && start > now;
          const pct = waiting
            ? 0
            : end > start
              ? Math.min(100, ((now - start) / (end - start)) * 100)
              : 100;
          return (
            <li
              key={item.id}
              data-queue-item={item.id}
              data-queue-state={waiting ? 'waiting' : 'building'}
              className="hud-cut-sm border border-line bg-void/60 p-3"
            >
              {/* Name and time on top, progress, then the buttons on their own line (16e feedback). */}
              <div className="flex items-baseline justify-between gap-3">
                <p className="min-w-0 break-words text-sm text-text">
                  {FACILITY_LABELS[item.facilityType]} → Lv {item.targetLevel}
                </p>
                <p className="shrink-0 font-mono text-xs tabular-nums text-info">
                  {/* The entry above shows when this one starts. */}
                  {waiting ? 'waiting' : end > now ? formatDuration(end - now) : 'finishing…'}
                </p>
              </div>
              <div className="mt-2 h-1 rounded-full bg-panel-raised">
                <div className="h-1 rounded-full bg-accent" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-1">
                {!waiting && actions?.finishNow && !isPendingId(item.id) ? (
                  <FinishNowButton
                    context={actions.finishNow}
                    kind="building"
                    itemId={item.id}
                    endsAt={end}
                    now={now}
                    title={`Finish ${FACILITY_LABELS[item.facilityType]} Lv ${item.targetLevel} now?`}
                  />
                ) : null}
                <Button
                  variant="ghost"
                  // A provisional entry has nothing to cancel on the server yet.
                  disabled={!actions || isPendingId(item.id)}
                  pending={actions?.pending.cancel === item.id}
                  title={waiting ? 'Not started yet: full refund' : 'Partial refund'}
                  onClick={() => actions?.cancel(item.id)}
                >
                  Cancel
                </Button>
              </div>
            </li>
          );
        })}
        {ordered.length < 2 ? (
          <li className="hud-cut-sm border border-dashed border-line p-3 text-sm text-faint">
            {ordered.length === 0
              ? 'Nothing is being built.'
              : 'You can queue one more upgrade — it starts when this one is done.'}
          </li>
        ) : null}
      </ul>
    </Panel>
  );
}

function FacilityRow({
  galaxyId,
  facility,
  level,
  effect,
  option,
  have,
  actions,
}: {
  galaxyId: string;
  facility: FacilityType;
  level: number;
  effect: string | null;
  option: UpgradeOption | undefined;
  have: Resources;
  actions: SystemActions | undefined;
}) {
  const canPay = option ? affordable(have, option.cost) : false;
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <li data-facility={facility} className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="truncate text-sm text-text">{FACILITY_LABELS[facility]}</span>
            <IconButton
              label={`About ${FACILITY_LABELS[facility]}`}
              className="-my-3 -mr-2 shrink-0"
              onClick={() => setInfoOpen(true)}
            >
              <Info className="size-4" aria-hidden />
            </IconButton>
          </div>
          <span className="font-mono text-xs tabular-nums text-muted">
            Lv {level}
            {effect ? <span className="text-faint"> · {effect}</span> : null}
          </span>
        </div>
        {!option ? (
          <span className="shrink-0 text-xs text-faint">Max level</span>
        ) : option.blocker ? (
          <span className="shrink-0 text-right text-xs text-muted">
            {blockerText(option.blocker, facility)}
          </span>
        ) : (
          <Button
            className="shrink-0 px-3"
            disabled={!actions || !canPay}
            pending={actions?.pending.upgrade === facility}
            onClick={() => actions?.upgrade(facility)}
          >
            Upgrade → Lv {option.targetLevel}
          </Button>
        )}
      </div>
      {infoOpen ? (
        <BuildingInfo
          galaxyId={galaxyId}
          facility={facility}
          currentLevel={level}
          open
          onClose={() => setInfoOpen(false)}
        />
      ) : null}
      {option ? (
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <CostList cost={option.cost} available={have} />
          <span className="font-mono text-xs text-faint">{formatDuration(option.durationMs)}</span>
        </div>
      ) : null}
    </li>
  );
}

export function BuildingsTab({
  galaxyId,
  system,
  have,
  actions,
}: {
  galaxyId: string;
  system: FullSystem;
  have: Resources;
  actions: SystemActions | undefined;
}) {
  return (
    <div className="grid gap-4 2xl:grid-cols-2">
      {FACILITY_GROUPS.map((group) => (
        <Panel key={group.id} title={group.label}>
          {group.id === 'production' ? (
            <div className="-mt-1 mb-3">
              <PlanetsLine system={system} />
            </div>
          ) : null}
          <ul className="divide-y divide-line">
            {group.facilities.map((facility) => (
              <FacilityRow
                key={facility}
                galaxyId={galaxyId}
                facility={facility}
                level={system.facilities[facility] ?? 0}
                effect={facilityEffect(facility, system)}
                option={system.upgrades[facility]}
                have={have}
                actions={actions}
              />
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}
